import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  IFormControl,
  InputAdornment,
  LinearProgress,
  TextField,
} from "@mui/material";
import {
  FilterList,
  ImportExport,
  MoreHoriz,
  Search,
  ViewWeekOutlined,
} from "@mui/icons-material";
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
} from "@mui/x-data-grid";
import NoDataOverlay from "../NoDataOverlay";
import SortModelButton from "../SortModelButton";
import clsx from "clsx";
import AppMenuBar from "../AppMenuBar";
import { TablePaginationActions } from "../CustomPagination";
import useDebounce from "../../../base/useDebounce";
import useMediaQuery from "../../../base/useMediaQuery";

/**
 * @author Sarthak Sharma.
 * @since 01-01-2024.
 * @description Detail data grid component.
 */

const DetailDataGrid = (props) => {
  const { onPressDelete, columns, onSearchTermChange, searchTerm } = props;
  const isMobile = useMediaQuery("(max-width: 600px)"); // Adjust breakpoint as needed
  const [sortModel, setSortModel] = useState([]);

  const onChangeSortModel = (model, details) => {
    setSortModel(model);
  };

  const optionData = [{ title: "Delete", onPress: onPressDelete }];

  const CustomToolbar = useCallback(
    ({ searchTerm, onChangeSearchTerm, optionData }) => {
      const onChangeSearch = (event) => {
        onChangeSearchTerm(event.target.value);
      };

      return (
        // <div className="flex gap-3 items-center px-3 mb-3">
        <div
          className={`flex gap-3 items-center px-3 mb-3 ${
            isMobile ? "flex-col" : ""
          }`}
        >
          <div className="w-full">
            <FormControl className="w-full rounded-[4px] border-2 border-solid border-[#e1e3e5]">
              <TextField
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                value={searchTerm}
                // defaultValue={searchTerm}
                onChange={onChangeSearch}
                type="search"
                size="small"
                placeholder="Search"
                className="text-[#5c5f62] text-[14px] rounded-[4px]"
              />
            </FormControl>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex border-[1px] border-[#AEB4B9] rounded-[4px]">
              <Button className="flex items-center gap-1">
                <GridToolbarFilterButton className="opacity-0 absolute" />
                <FilterList className="text-[22px] text-neutral-800" />
                <div className="text-neutral-800 text-sm font-['Poppins-Regular'] leading-tight">
                  Filter
                </div>
              </Button>
              <Divider
                orientation="vertical"
                flexItem
                className="bg-[#AEB4B9]"
              />
              <Button className="flex items-center gap-1">
                <GridToolbarColumnsButton className="opacity-0 absolute" />
                <ViewWeekOutlined className="text-[22px] text-neutral-800" />
                <div className="text-neutral-800 text-sm font-['Poppins-Regular'] leading-tight">
                  Column
                </div>
              </Button>
              <Divider
                orientation="vertical"
                flexItem
                className="bg-[#AEB4B9]"
              />
              <SortModelButton
                columns={columns}
                sortModel={sortModel}
                onChangeSortModel={onChangeSortModel}
              >
                <div className="flex items-center gap-1">
                  <ImportExport className="text-[22px] text-neutral-800" />
                  <div className="text-neutral-800 text-sm font-['Poppins-Regular'] leading-tight">
                    Sort
                  </div>
                </div>
              </SortModelButton>
            </div>
            <div className="flex border-[1px] border-[#AEB4B9] rounded-[4px]">
              <AppMenuBar options={optionData}>
                <MoreHoriz className="text-[22px] text-neutral-800" />
              </AppMenuBar>
            </div>
          </div>
        </div>
      );
    },
    []
  );

  return (
    <Box className="flex flex-col gap-1 py-5 px-1 w-full">
      <Box
        className="data-grid-2"
        sx={{
          height: props.rows?.length <= 12 ? "fit" : 600,
          width: "100%",
          overflowY: "hidden",
        }}
      >
        <DataGrid
          {...props}
          autoHeight={props.rows?.length <= 12}
          slots={{
            toolbar: CustomToolbar,
            noResultsOverlay: NoDataOverlay,
            loadingOverlay: LinearProgress,
            noRowsOverlay: NoDataOverlay,
          }}
          slotProps={{
            noRowsOverlay: {
              title: "No Data found",
            },
            noResultsOverlay: {
              title: "No Result found",
            },
            pagination: {
              ActionsComponent: TablePaginationActions,
            },
            toolbar: {
              searchTerm: searchTerm,
              onChangeSearchTerm: onSearchTermChange,
              optionData: optionData,
            },
          }}
          sortModel={sortModel}
          onSortModelChange={onChangeSortModel}
          paginationMode="server"
          pageSizeOptions={[5, 10, 20, 50, 75, 100]}
          checkboxSelection
        />
      </Box>
    </Box>
  );
};

export default DetailDataGrid;
